/**
 * Evidence validity and its lifecycle state.
 *
 * WHY THE STATE IS DERIVED AND NOT STORED
 * A control that reads COMPLIANT on the strength of a certificate that lapsed
 * in June is the most dangerous output a GRC tool can produce, because it is
 * confidently wrong. The cheapest way to guarantee that never happens is to
 * make the lifecycle state a pure function of two dates and the current time,
 * computed identically by the API, the nightly job and the web client — all
 * three importing THIS function.
 *
 * A stored status column would be a second copy of the truth, and a second copy
 * can drift: the job fails for a week, or an administrator edits `validUntil`
 * and the column is not recomputed, and the screen goes back to saying green.
 * Derivation has no such failure mode. Nothing here can be stale.
 *
 * What IS stored is bookkeeping, not truth: `Evidence.lifecycleNotifiedState`
 * records the last state a notification was sent for, so the job notifies once
 * per transition instead of once per run. Losing that column would cause a
 * duplicate e-mail, never a false green.
 *
 * DAY GRANULARITY
 * `validUntil` is INCLUSIVE and interpreted to the end of that local day: a
 * certificate "valid until 30 June" is good for the whole of 30 June. Treating
 * it as an instant would expire such evidence at midnight of the morning it is
 * still valid, which every auditor would read as a bug.
 */
/**
 * The lifecycle of one piece of evidence.
 *
 *   NOT_TRACKED    No `validUntil`. Most evidence — a screenshot, a config
 *                  export — has no meaningful expiry, and this is why the
 *                  columns are nullable. NOT_TRACKED is NOT a failure: it means
 *                  "nobody claimed this expires", and it must never be
 *                  presented as if it had expired.
 *   SCHEDULED      `validFrom` is in the future. The document exists but is not
 *                  yet in force, so it cannot support a control today.
 *   VALID          In force, and the expiry is further away than the warning
 *                  window.
 *   EXPIRING_SOON  In force, but inside the warning window. Actionable: this is
 *                  the state that gives someone time to renew.
 *   EXPIRED        Past `validUntil`. Anything relying on it is a false green.
 */
export const EVIDENCE_LIFECYCLE_STATES = [
    'NOT_TRACKED',
    'SCHEDULED',
    'VALID',
    'EXPIRING_SOON',
    'EXPIRED',
];
/** The states that should draw someone's attention. */
export const EVIDENCE_AT_RISK_STATES = [
    'EXPIRING_SOON',
    'EXPIRED',
];
/**
 * Default warning window, in days, before `validUntil`.
 *
 * Thirty days is the shortest notice that is actually useful: re-issuing a
 * penetration test or a signed DPA involves a third party and rarely happens in
 * a week. Override per deployment with EVIDENCE_EXPIRY_WARNING_DAYS.
 */
export const DEFAULT_EVIDENCE_EXPIRY_WARNING_DAYS = 30;
export const MS_PER_DAY = 86_400_000;
function toDate(value) {
    if (value === null || value === undefined)
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/** Local midnight at the start of `date`'s day. */
export function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}
/** The last instant of `date`'s day. See "DAY GRANULARITY" above. */
export function endOfDay(date) {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}
/**
 * The lifecycle state of one piece of evidence. Pure; no I/O.
 *
 * Order of the checks matters. SCHEDULED is tested before EXPIRED so that a
 * nonsensical pair (a future `validFrom` with a past `validUntil`, which the
 * API refuses but old or imported data might hold) reads as "not in force"
 * rather than as a confident verdict about a window that never opened.
 */
export function evidenceLifecycleState(evidence, options = {}) {
    const now = options.now ?? new Date();
    const warningDays = options.warningDays ?? DEFAULT_EVIDENCE_EXPIRY_WARNING_DAYS;
    const validFrom = toDate(evidence.validFrom);
    const validUntil = toDate(evidence.validUntil);
    if (validFrom && startOfDay(validFrom).getTime() > now.getTime())
        return 'SCHEDULED';
    // No expiry claimed. Deliberately not a problem state.
    if (!validUntil)
        return 'NOT_TRACKED';
    const expiresAt = endOfDay(validUntil).getTime();
    if (now.getTime() > expiresAt)
        return 'EXPIRED';
    if (expiresAt - now.getTime() <= warningDays * MS_PER_DAY)
        return 'EXPIRING_SOON';
    return 'VALID';
}
/**
 * Whole days until expiry, counted in calendar days so the number matches what
 * a person reading a date would say.
 *
 * Positive: days remaining. 0: expires today. Negative: days since expiry.
 * Null when there is no expiry to count towards.
 */
export function daysUntilExpiry(evidence, now = new Date()) {
    const validUntil = toDate(evidence.validUntil);
    if (!validUntil)
        return null;
    return Math.round((startOfDay(validUntil).getTime() - startOfDay(now).getTime()) / MS_PER_DAY);
}
/**
 * Reject a window that ends before it starts.
 *
 * Returned as a boolean rather than thrown so the same check can drive a form
 * hint on the client and a 400 on the server.
 */
export function isValidityWindowOrdered(evidence) {
    const from = toDate(evidence.validFrom);
    const until = toDate(evidence.validUntil);
    if (!from || !until)
        return true;
    return startOfDay(from).getTime() <= startOfDay(until).getTime();
}
/**
 * Roll a list of evidence up into the one sentence a reviewer needs: is this
 * claim still supported?
 */
export function summarizeEvidenceLifecycle(items, options = {}) {
    const now = options.now ?? new Date();
    const summary = {
        total: items.length,
        tracked: 0,
        expired: 0,
        expiringSoon: 0,
        valid: 0,
        scheduled: 0,
        notTracked: 0,
        allEvidenceExpired: false,
        nextExpiryAt: null,
        daysToNextExpiry: null,
    };
    let soonest = null;
    for (const item of items) {
        const state = evidenceLifecycleState(item, { ...options, now });
        if (state !== 'NOT_TRACKED')
            summary.tracked += 1;
        switch (state) {
            case 'EXPIRED':
                summary.expired += 1;
                break;
            case 'EXPIRING_SOON':
                summary.expiringSoon += 1;
                break;
            case 'VALID':
                summary.valid += 1;
                break;
            case 'SCHEDULED':
                summary.scheduled += 1;
                break;
            case 'NOT_TRACKED':
                summary.notTracked += 1;
                break;
        }
        if (state === 'EXPIRING_SOON' || state === 'VALID') {
            const until = toDate(item.validUntil);
            if (until && (!soonest || until.getTime() < soonest.getTime()))
                soonest = until;
        }
    }
    if (soonest) {
        summary.nextExpiryAt = soonest.toISOString();
        summary.daysToNextExpiry = daysUntilExpiry({ validUntil: soonest }, now);
    }
    // `total > 0` guards the vacuous case: no evidence at all is a different
    // problem (nothing was ever attached) and is reported elsewhere. Saying
    // "all the evidence has expired" about an empty list would be a lie.
    summary.allEvidenceExpired =
        summary.total > 0 && summary.expired > 0 && summary.expired === summary.total;
    return summary;
}
//# sourceMappingURL=evidenceLifecycle.js.map